import { SUPABASE_URL } from "@/integrations/supabase/client";

type AnalyticsConfig = {
  enabled: boolean;
  measurementId: string | null;
  environment: string;
};

type SafeAnalyticsValue = string | number | boolean | null;

declare global {
  interface Window {
    dataLayer?: Array<IArguments | unknown[]>;
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_SCRIPT_ID = "applyt-ga4-script";
const ANALYTICS_CONFIG_PATH = "/functions/v1/analytics-config";
const SAFE_EVENT_NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const SAFE_PARAM_KEYS = new Set([
  "action",
  "artifact_kind",
  "format",
  "is_authenticated",
  "lens_id",
  "lens_type",
  "message_kind",
  "mode",
  "page_path",
  "reason",
  "source",
  "status",
]);
const SAFE_QUERY_KEYS = new Set(["method", "tab"]);
const SENSITIVE_KEY_PATTERN =
  /(content|email|invite|name|password|payment|phone|prompt|query|secret|text|token|user)/i;
const PLUGIN_PATH = "/plugins/pricing-power-advisor";

export const isPricingPowerAdvisorPage = (pathname: string) =>
  pathname === PLUGIN_PATH || pathname.startsWith(`${PLUGIN_PATH}/`);

let initPromise: Promise<boolean> | null = null;
let activeMeasurementId: string | null = null;
let lastTrackedPageView: string | null = null;

const hasBrowserGlobals = () =>
  typeof window !== "undefined" && typeof document !== "undefined" && typeof fetch === "function";

const isAnalyticsConfig = (value: unknown): value is AnalyticsConfig => {
  if (!value || typeof value !== "object") return false;
  const config = value as Partial<AnalyticsConfig>;
  return (
    typeof config.enabled === "boolean" &&
    (typeof config.measurementId === "string" || config.measurementId === null) &&
    typeof config.environment === "string"
  );
};

const getAnalyticsConfig = async (): Promise<AnalyticsConfig | null> => {
  const response = await fetch(`${SUPABASE_URL}${ANALYTICS_CONFIG_PATH}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });

  if (!response.ok) return null;

  const data = await response.json().catch(() => null);
  return isAnalyticsConfig(data) ? data : null;
};

const loadGtagScript = (measurementId: string) =>
  new Promise<void>((resolve, reject) => {
    const scriptSrc = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
    const existingScript = document.getElementById(GA_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existingScript || document.createElement("script");

    if (existingScript?.src === scriptSrc) {
      resolve();
      return;
    }

    script.addEventListener("load", () => {
      script.dataset.loaded = "true";
      resolve();
    }, { once: true });
    script.addEventListener("error", () => reject(new Error("Google Analytics script failed to load")), { once: true });

    if (!existingScript) {
      script.id = GA_SCRIPT_ID;
      script.async = true;
      script.src = scriptSrc;
      document.head.appendChild(script);
    } else if (existingScript.src !== scriptSrc) {
      existingScript.dataset.loaded = "false";
      existingScript.src = scriptSrc;
    }
  });

const ensureGtag = async (measurementId: string) => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag(...args: unknown[]) { window.dataLayer?.push(args); };

  if (activeMeasurementId !== measurementId) {
    window.gtag("js", new Date());
    window.gtag("config", measurementId, { send_page_view: false });
  }

  await loadGtagScript(measurementId);
  activeMeasurementId = measurementId;
};

const normalizePathname = (pathname: string) => {
  if (/^\/chat\/(?!new(?:\/|$))[^/]+/.test(pathname)) {
    return pathname.replace(/^\/chat\/[^/]+/, "/chat/:conversation_id");
  }
  if (/^\/board\/[^/]+/.test(pathname)) {
    return pathname.replace(/^\/board\/[^/]+/, "/board/:conversation_id");
  }
  if (/^\/lens\/[^/]+/.test(pathname)) {
    return pathname.replace(/^\/lens\/[^/]+/, "/lens/:lens_id");
  }
  return pathname;
};

const sanitizePagePath = (path: string) => {
  if (!hasBrowserGlobals()) return path || "/";

  try {
    const url = new URL(path || "/", window.location.origin);
    const safePathname = normalizePathname(url.pathname);
    const safeParams = new URLSearchParams();

    url.searchParams.forEach((value, key) => {
      if (!SAFE_QUERY_KEYS.has(key)) return;
      if (!/^[a-zA-Z0-9_-]{1,96}$/.test(value)) return;
      safeParams.set(key, value);
    });

    const query = safeParams.toString();
    return query ? `${safePathname}?${query}` : safePathname;
  } catch {
    return "/";
  }
};

const sanitizeEventParams = (params?: Record<string, unknown>): Record<string, SafeAnalyticsValue> => {
  const safeParams: Record<string, SafeAnalyticsValue> = {};
  if (!params) return safeParams;

  Object.entries(params).forEach(([key, value]) => {
    if (!SAFE_PARAM_KEYS.has(key) || SENSITIVE_KEY_PATTERN.test(key)) return;
    if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
      if (key === "page_path" && typeof value === "string") {
        safeParams[key] = sanitizePagePath(value);
      } else if (typeof value === "string") {
        safeParams[key] = value.slice(0, 128);
      } else {
        safeParams[key] = value as SafeAnalyticsValue;
      }
    }
  });

  return safeParams;
};

export const initAnalytics = () => {
  if (!hasBrowserGlobals() || isPricingPowerAdvisorPage(window.location.pathname)) return Promise.resolve(false);
  if (activeMeasurementId && window.gtag) return Promise.resolve(true);
  if (initPromise) return initPromise;

  initPromise = getAnalyticsConfig()
    .then(async (config) => {
      if (isPricingPowerAdvisorPage(window.location.pathname)) {
        initPromise = null;
        return false;
      }
      if (!config?.enabled || !config.measurementId) return false;
      await ensureGtag(config.measurementId);
      return true;
    })
    .catch((error) => {
      console.warn("Analytics initialization skipped:", error);
      return false;
    });

  return initPromise;
};

export const trackPageView = (path: string, title?: string) => {
  if (!hasBrowserGlobals()) return;

  const pagePath = sanitizePagePath(path);
  if (isPricingPowerAdvisorPage(window.location.pathname) || isPricingPowerAdvisorPage(pagePath.split("?")[0])) {
    lastTrackedPageView = null;
    return;
  }
  if (lastTrackedPageView === pagePath) return;
  lastTrackedPageView = pagePath;

  void initAnalytics().then((enabled) => {
    if (!enabled || !window.gtag || isPricingPowerAdvisorPage(window.location.pathname)) return;

    const pageLocation = new URL(pagePath, window.location.origin).toString();
    if (!activeMeasurementId) return;

    window.gtag("config", activeMeasurementId, {
      page_path: pagePath,
      page_location: pageLocation,
      page_title: title || document.title,
    });
  });
};

export const trackEvent = (name: string, params?: Record<string, unknown>) => {
  if (!hasBrowserGlobals() || isPricingPowerAdvisorPage(window.location.pathname) || !SAFE_EVENT_NAME_PATTERN.test(name)) return;

  void initAnalytics().then((enabled) => {
    if (!enabled || !window.gtag || !activeMeasurementId || isPricingPowerAdvisorPage(window.location.pathname)) return;
    window.gtag("event", name, {
      ...sanitizeEventParams(params),
      send_to: activeMeasurementId,
    });
  });
};
